import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API_URL } from "../data/site";

const WELCOME = {
  role: "assistant",
  text: "Welcome to ABS LLC. Ask me anything about arrive-and-drive programs, driver development, logistics, or trackside support — or leave your name and email and the team will reply within one business day.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useRef(
    localStorage.getItem("abs-chat-session") ||
      (() => {
        const id = `s-${crypto.randomUUID()}`;
        localStorage.setItem("abs-chat-session", id);
        return id;
      })()
  );
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const snapshot = acc;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: snapshot };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Sorry — I'm unavailable right now. Please use the inquiry form or email Alextouring@gmail.com.",
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="chat-widget-button"
        aria-label={open ? "Close chat" : "Open chat"}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center bg-[#111111] text-white shadow-xl transition-colors duration-300 hover:bg-[#e63946]"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-[84px] right-5 z-50 flex h-[70vh] max-h-[560px] w-[calc(100vw-40px)] max-w-sm flex-col border border-neutral-200 bg-white shadow-2xl"
        >
          <div className="grain relative bg-[#111111] px-5 py-4">
            <p className="font-overline text-[9px] uppercase tracking-[0.3em] text-[#e63946]">ABS LLC</p>
            <p className="font-display mt-1 text-lg font-bold tracking-tight text-white">Concierge Assistant</p>
            <p className="text-xs text-neutral-400">Answers about programs, circuits & logistics</p>
          </div>

          <div data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#111111] text-white"
                      : "border border-neutral-200 bg-[#f8f9fa] text-[#111111]"
                  }`}
                >
                  {m.text || (sending && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="flex items-center gap-2 border-t border-neutral-200 p-3">
            <input
              data-testid="chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about programs, circuits…"
              aria-label="Chat message"
              className="flex-1 border border-neutral-300 bg-white px-3 py-2.5 text-sm text-[#111111] placeholder:text-neutral-400 focus:border-[#111111] focus:outline-none"
            />
            <button
              type="submit"
              data-testid="chat-send-button"
              disabled={sending || !input.trim()}
              aria-label="Send message"
              className="flex h-10 w-10 items-center justify-center bg-[#111111] text-white transition-colors duration-300 hover:bg-[#e63946] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};
